import { api } from "@/lib/api";

/**
 * One person on the roster.
 *
 * ⚠️ `canAdminister` comes from the SERVER, per row. It folds together the caller's own
 * permissions, scopes and clearances against this person's — you may not administer
 * anyone whose access is not contained within your own — and the browser has no way to
 * work that out. Deriving privilege client-side is defect D1, and every endpoint
 * re-checks this regardless, so it governs rendering only.
 */
export interface Person {
  id: string;
  displayName: string;
  email: string | null;
  jobTitle: string | null;
  department: string | null;
  siteName: string | null;
  siteId: string | null;
  /** True when an administrator placed them, so directory sync will leave the site alone. */
  siteAssignmentIsManual: boolean;
  managerName: string | null;
  managerUserId: string | null;
  /** IANA id, or null meaning "inherit the site's". Display only — SLA uses the site's. */
  timeZoneId: string | null;
  /** Three states: null follows the title rules, true and false pin the answer. */
  isVipOverride: boolean | null;

  /**
   * From the directory, read-only. Azure is the source of truth, so editing any of these
   * here would survive until the next sync and then silently revert.
   *
   * ⚠️ `mobilePhone` is personal data. Shown to staff who already hold `user.manage`, and
   * never logged.
   */
  businessPhone: string | null;
  mobilePhone: string | null;

  /**
   * What this person entered themselves, and whether they agreed to be texted.
   *
   * ⚠️ Different fields from the two directory ones above, shown beside them rather than
   * instead of them — sync overwrites those on every run and cannot touch these. Read-only
   * here, and the consent especially so: there is no path by which an administrator agrees
   * to text somebody on their behalf, and adding one would make the record worthless.
   */
  contactPhone: string | null;
  contactPhoneExtension: string | null;
  smsConsent: boolean;

  employeeType: string | null;
  employeeHireDate: string | null;
  employeeId: string | null;

  isActive: boolean;
  isExternalGuest: boolean;
  /** Whether a picture exists AND has passed a scan. Avoids requesting one that 404s. */
  hasAvatar: boolean;
  /**
   * Roles held ACROSS THE WHOLE SYSTEM. Not every role they hold — see `teams`.
   *
   * ⚠️ This list used to be the only role chips on the roster, and the gap it left is the
   * defect that produced all of this: somebody whose administrative role was held on a team
   * showed as "Requester" beside an Administrator badge, and the administrator who had just
   * demoted them read the chip as proof the demotion worked.
   */
  roles: readonly string[];

  /** The teams they are on, each with the role held there. */
  teams: readonly PersonTeam[];

  /**
   * True when they hold at least one administrative capability — globally, through the role
   * they hold on a team, or through an active access grant.
   *
   * ⚠️ Decided by the SERVER, from permissions rather than role names — the browser sees
   * role names only and could not tell an invented "Service Desk Lead" that grants
   * everything from an "Admin Assistant" that grants nothing. Display only.
   */
  isAdministrator: boolean;
  canAdminister: boolean;
}

/** One team someone belongs to, and what they are on it. */
export interface PersonTeam {
  teamId: string;
  teamName: string;
  roleId: string;
  roleName: string;

  /**
   * Whether the role held HERE is what makes them an administrator.
   *
   * ⚠️ The server's answer, from the permissions that role carries. This is the fact the
   * roster was missing: a team chip that cannot say "this membership is the administrative
   * one" leaves an Administrator badge with no visible cause.
   */
  isAdministrative: boolean;
}

// ---------------------------------------------------------------------------
// Administrative access, and removing it.
// ---------------------------------------------------------------------------

/** Where an administrative capability reaches somebody from. */
export type AdministrativeGrantSource = "GlobalRole" | "TeamRole" | "AccessGrant";

export interface AdministrativeGrant {
  source: AdministrativeGrantSource;
  id: string;
  roleId: string;
  roleName: string;
  teamId: string | null;
  teamName: string | null;
  /** Only the ADMINISTRATIVE permissions the role carries — the answer to "why". */
  permissions: readonly string[];
}

export interface HandoverCandidate {
  userId: string;
  displayName: string;
  email: string | null;
}

/** What has to happen to one team's work before this person leaves it. */
export interface TeamHandover {
  teamId: string;
  teamName: string;
  /** A count, never a list. A demotion screen has no reason to carry ticket subjects. */
  openTicketCount: number;
  candidates: readonly HandoverCandidate[];

  /**
   * False when the CALLER cannot see this team's queue.
   *
   * ⚠️ Not a technicality. Both enforcement layers scope tickets to the caller and RLS has
   * no bypass, so a count taken outside their reach comes back as zero rather than as an
   * error — "none" and "I cannot see" are the same answer. The demotion refuses on this
   * rather than guessing, and the UI must say so instead of showing "0 requests".
   */
  visible: boolean;
}

export interface AdministrativeAccess {
  userId: string;
  displayName: string;
  isAdministrator: boolean;
  canAdminister: boolean;
  grants: readonly AdministrativeGrant[];
  handovers: readonly TeamHandover[];
}

/** What a revocation left behind. */
export interface ResidualAdministration {
  isStillAdministrator: boolean;
  grants: readonly AdministrativeGrant[];
}

/** What to do with one team's work. Exactly one of the two must be chosen. */
export interface TeamHandoverDecision {
  teamId: string;
  reassignToUserId: string | null;
  returnToQueue: boolean;
}

export function fetchAdministrativeAccess(userId: string): Promise<AdministrativeAccess> {
  return api<AdministrativeAccess>(`/api/users/${userId}/administrative-access`);
}

/**
 * Removes every administrative capability this person holds, from all three sources.
 *
 * ⚠️ The command cannot name which grants to strip, deliberately. A caller who could
 * enumerate them could also omit one, which is the partial revoke this endpoint exists to
 * replace. The server decides what is administrative, from the permission catalog.
 */
export function demoteAdministrator(
  userId: string,
  justification: string,
  handovers: readonly TeamHandoverDecision[],
): Promise<ResidualAdministration> {
  return api<ResidualAdministration>(`/api/users/${userId}/demote`, {
    method: "POST",
    body: { justification, handovers },
  });
}

export interface PeopleList {
  items: Person[];
  /** How many matched, not how many were returned. The page is capped server-side. */
  totalMatching: number;
  /** False when Graph is not configured here — distinct from "nobody matched". */
  directorySearchAvailable: boolean;

  /**
   * Pass back as `cursor` for the next page. Null on the last one.
   *
   * ⚠️ Keyset, not an offset. With thousands of people and a sync that adds and
   * deactivates them, an offset shifts every later row the moment anything changes —
   * paging would silently skip people and repeat others.
   */
  nextCursor: string | null;
}

export type PersonStatus = "active" | "inactive" | "all";

/** Somebody the directory knows about, who may or may not have signed in here yet. */
export interface DirectoryHit {
  objectId: string;
  displayName: string;
  email: string | null;
  jobTitle: string | null;
  department: string | null;
  companyName: string | null;
  alreadyHere: boolean;
  existingUserId: string | null;
}

export function getPeople(query: {
  search?: string;
  siteId?: string;
  status?: PersonStatus;
  /** True for administrators only, false for everybody else, omitted for both. */
  isAdministrator?: boolean;
  includeInactive?: boolean;
  pageSize?: number;
  cursor?: string;
}): Promise<PeopleList> {
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.siteId) params.set("siteId", query.siteId);
  if (query.status) params.set("status", query.status);

  // ⚠️ Undefined, not false, is "everybody" — so this tests for undefined rather than
  // truthiness. `if (query.isAdministrator)` would silently drop the "non-administrators"
  // filter and quietly return the whole roster instead.
  if (query.isAdministrator !== undefined) {
    params.set("isAdministrator", String(query.isAdministrator));
  }

  if (query.includeInactive) params.set("includeInactive", "true");
  if (query.pageSize) params.set("pageSize", String(query.pageSize));
  if (query.cursor) params.set("cursor", query.cursor);

  const qs = params.toString();
  return api<PeopleList>(`/api/people${qs ? `?${qs}` : ""}`);
}

/**
 * One person, by id.
 *
 * For screens that hold an id and need a name — the ticket list filtered to somebody, or
 * a request being raised on their behalf. Both previously fetched a page of the roster and
 * searched it client-side, which found nobody past the page cap.
 */
export function getPerson(userId: string): Promise<Person> {
  return api<Person>(`/api/people/${encodeURIComponent(userId)}`);
}

/**
 * Details only. No role, no team, no active flag — deliberately, and this stays true even
 * though the details panel now offers role grants beside these fields.
 *
 * ⚠️ A grant is a separate command to a separate endpoint (`POST /api/users/{id}/roles`),
 * bounded by RoleGrantGuard and audited as a permission change. Keeping it off this DTO is
 * what makes overposting a role structurally impossible rather than merely refused: there
 * is no field to overpost.
 */
export interface UpdatePerson {
  siteId?: string | null;
  /** Hands the site back to directory sync. Distinct from setting it to nothing. */
  returnSiteToDirectory?: boolean;
  managerUserId?: string | null;
  timeZoneId?: string | null;
  isVipOverride?: boolean | null;
}

export function updatePerson(userId: string, body: UpdatePerson): Promise<void> {
  return api<void>(`/api/people/${encodeURIComponent(userId)}`, { method: "PATCH", body });
}

/**
 * Searches the directory for somebody who may have no account here yet.
 *
 * Three characters minimum, matching the server — a shorter term matches a large fraction
 * of any real directory, and this is a tenant-wide lookup rather than a local one.
 */
export function searchDirectory(q: string): Promise<DirectoryHit[]> {
  return api<DirectoryHit[]>(`/api/people/directory-search?q=${encodeURIComponent(q)}`);
}

export const DirectorySearchMinLength = 3;

/**
 * Creates the local record for somebody the directory knows but who has never signed in,
 * so they can be added to a ticket, named as a manager, or given a site.
 *
 * ⚠️ Sends an OBJECT ID and nothing else. The server re-reads everything about them from
 * Graph — a name or an email accepted from here would let the browser mint a user row
 * describing whoever it liked. They land on the baseline role, exactly as a first sign-in
 * would leave them.
 */
export function addFromDirectory(objectId: string): Promise<{
  userId: string;
  displayName: string;
}> {
  return api<{ userId: string; displayName: string }>(
    `/api/people/from-directory/${encodeURIComponent(objectId)}`,
    { method: "POST" },
  );
}

/**
 * Somebody a request can be raised FOR (D-51), as the picker draws them.
 *
 * ⚠️ A different shape and a different endpoint from `Person`, deliberately. `Person` comes
 * from `/api/people`, which is fenced to `user.manage` wholesale — the desk agents this
 * picker exists for do not hold it, and the form used to call that endpoint and fail to put
 * a name to the id it was about to file against. This carries a name, a face, a job title,
 * an email and a site: enough to tell two John Smiths apart across twenty-nine sites, and
 * nothing that describes the organisation around them.
 */
export interface RequesterCandidate {
  userId: string;
  displayName: string;
  email: string | null;
  jobTitle: string | null;
  siteName: string | null;
  /** Where they are placed, so the site picker can open on their own facility (D-104). */
  siteId: string | null;
  hasAvatar: boolean;
  /** Presentation only (D-34). Grants nothing and gates nothing. */
  isVip: boolean;
}

export interface RequesterCandidateList {
  /** True when nothing was searched because too little was typed — not "no matches". */
  searchRequired: boolean;
  items: RequesterCandidate[];
}

/**
 * Whether two user ids name the same person, whichever way each was written.
 *
 * ⚠️ THIS EXISTS BECAUSE THE PRODUCT PUBLISHES IDS IN TWO SHAPES and `===` cannot see past
 * the difference. Most person-shaped responses serialise a `Guid`, which lands dashed
 * (`0a1b2c3d-4e5f-…`); the pickers publish `Guid.ToString("N")`, which is the same value
 * with the dashes removed. Both are the id. Compared with `===` they are never equal.
 *
 * The comparison that caught it was the "you have chosen the person it already belongs to"
 * guard on the requester-correction dialog, which had therefore never once fired. Nothing
 * broke visibly — the server answers a no-op correction cheerfully — which is exactly why it
 * survived: a guard that silently never fires looks identical to one with nothing to catch.
 *
 * ⚠️ Comparison only. It is deliberately NOT a normaliser used before sending: what goes on
 * the wire is each endpoint's own contract, and the server parses both forms with
 * `Guid.TryParse`. Reshaping ids in the browser would put a second opinion about the format
 * in the one place that has no tests over it.
 */
export function sameUserId(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;

  return a.replace(/-/g, "").toLowerCase() === b.replace(/-/g, "").toLowerCase();
}

/**
 * ⚠️ The server refuses this to anybody who may not raise a request for somebody else, so
 * calling it from a screen that should not offer it returns 403 rather than a roster. The
 * `canRaiseForOthers` flag on the session decides whether the control is DRAWN; this is
 * what decides whether it answers.
 */
export function searchRequesterCandidates(q: string): Promise<RequesterCandidateList> {
  return api<RequesterCandidateList>(
    `/api/tickets/requester-candidates?q=${encodeURIComponent(q)}`,
  );
}

/** Resolves one by id, so a `?forUser=` in the URL can be given a name before anyone types. */
export function getRequesterCandidate(userId: string): Promise<RequesterCandidate> {
  return api<RequesterCandidate>(`/api/tickets/requester-candidates/${encodeURIComponent(userId)}`);
}
